import MastercopyABI from "@/common/config/abi/GnosisSafeMastercopy.json";
import ProxyFactoryABI from "@/common/config/abi/GnosisSafeProxyFactory.json";
import { hexlify } from "@/common/lib/utils";
import Config from "@/common/config";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const EMPTY_DATA = "0x";
const DEFAULT_FALLBACK_HANDLER_ADDRESS =
  "0xd5D82B6aDDc9027B22dCA772Aa68D5d74cdBdF44";

const state = {};

const actions = {
  async getNonce({ rootGetters }, address) {
    const web3 = rootGetters["appConfig/web3"]();
    return web3.eth.getTransactionCount(address, "pending");
  },
  async loadSafeInfo({ rootGetters }, safeAddress) {
    const web3 = rootGetters["appConfig/web3"]();
    const gnosisSafeContract = new web3.eth.Contract(
      MastercopyABI,
      safeAddress
    );
    const [owners, threshold] = await Promise.all([
      gnosisSafeContract.methods.getOwners().call(),
      gnosisSafeContract.methods.getThreshold().call(),
    ]);
    return { owners, threshold };
  },
  async getCreateSafeRawTx({ rootGetters, dispatch }, payload) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const web3 = rootGetters["appConfig/web3"]();
    const mastercopyContract = new web3.eth.Contract(
      MastercopyABI,
      Config.gnosis.mastercopyAddress
    );
    const proxyFactoryContract = new web3.eth.Contract(
      ProxyFactoryABI,
      Config.gnosis.proxyFactoryAddress,
      { from: payload.creator.address }
    );
    const gnosisSafeData = mastercopyContract.methods
      .setup(
        payload.owners.map(owner => owner.address),
        payload.threshold,
        ZERO_ADDRESS,
        EMPTY_DATA,
        DEFAULT_FALLBACK_HANDLER_ADDRESS,
        ZERO_ADDRESS,
        0,
        ZERO_ADDRESS
      )
      .encodeABI();
    const createProxyWithNonceMethod = proxyFactoryContract.methods.createProxyWithNonce(
      mastercopyContract.options.address,
      gnosisSafeData,
      new Date().valueOf()
    );
    const [nonce, gasLimit, { fast }] = await Promise.all([
      dispatch("getNonce", payload.creator.address),
      createProxyWithNonceMethod.estimateGas(),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    return {
      from: payload.creator.address,
      to: proxyFactoryContract.options.address,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: createProxyWithNonceMethod.encodeABI(),
    };
  },
  async fetchSafeAddressByTxHash({ rootGetters }, txHash) {
    const web3 = rootGetters["appConfig/web3"]();
    const receipt = await web3.eth.getTransactionReceipt(txHash);
    if (!receipt) return { failed: false };
    if (!receipt.status) return { failed: true };
    return {
      failed: false,
      safeAddress: "0x" + receipt?.logs[0].data.slice(-40),
    };
  },
  async broadcastTx({ rootGetters }, signedTx) {
    const web3 = rootGetters["appConfig/web3"]();
    return web3.eth.sendSignedTransaction(signedTx);
  },
};

const mutations = {};

const getters = {
  wallet: (state, getters, rootState, rootGetters) => {
    return rootGetters["userWallets/walletList"].result?.find(
      w => w.type === "gnosis"
    );
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
